import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import DeleteForever from "@mui/icons-material/DeleteForever";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Edit from "@mui/icons-material/Edit";
import Search from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BoltIcon from "@mui/icons-material/Bolt";
import DownloadDoneIcon from "@mui/icons-material/DownloadDone";
import PersonIcon from "@mui/icons-material/Person";
import React, { useCallback, useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TagModal from "../../components/TagModal";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import openSocket from "../../services/socket-io";

const reducer = (state, action) => {
  if (action.type === "LOAD_TAGS") {
    const tags = action.payload;
    const newTags = [];
    tags.forEach((tag) => {
      const tagIndex = state.findIndex((s) => s.id === tag.id);
      if (tagIndex !== -1) { state[tagIndex] = tag; }
      else { newTags.push(tag); }
    });
    return [...state, ...newTags];
  }
  if (action.type === "UPDATE_TAGS") {
    const tag = action.payload;
    const tagIndex = state.findIndex((s) => s.id === tag.id);
    if (tagIndex !== -1) { state[tagIndex] = tag; return [...state]; }
    return [tag, ...state];
  }
  if (action.type === "DELETE_TAG") {
    return state.filter((s) => s.id !== action.payload);
  }
  if (action.type === "RESET") return [];
};

const MainPaper = styled(Paper)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  margin: theme.spacing(1),
  overflowY: "scroll",
  ...theme.scrollbarStyles,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
}));

const SectionHeader = styled(Box)(({ theme, imported }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 1, 0.5),
  marginTop: imported ? theme.spacing(2) : 0,
}));

const TagRow = ({ tag, onEdit, onDelete, onViewContacts, t }) => {
  const theme = useTheme();
  return (
    <TableRow key={tag.id} hover>
      <TableCell align="center">{tag.id}</TableCell>
      <TableCell align="center">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
          {tag.name}
          {tag.isImported && (
            <Chip
              label="importada"
              size="small"
              icon={<BoltIcon style={{ fontSize: 11 }} />}
              sx={{
                height: 18,
                fontSize: "0.62rem",
                fontWeight: 700,
                backgroundColor: theme.palette.primary.main + "18",
                color: theme.palette.primary.main,
                border: `1px solid ${theme.palette.primary.main}40`,
                "& .MuiChip-label": { px: 0.6 },
              }}
            />
          )}
        </Box>
      </TableCell>
      <TableCell align="center">
        <Box sx={{ backgroundColor: tag.color, width: 40, height: 20, margin: "0 auto", borderRadius: 10 }} />
      </TableCell>
      <TableCell align="center">
        <Tooltip title={t("tags.buttons.viewContacts", { defaultValue: "Ver contatos" })}>
          <Chip
            label={tag.contacttag?.length || 0}
            onClick={() => onViewContacts(tag)}
            icon={<VisibilityIcon />}
            color="primary"
            variant="outlined"
            sx={{ cursor: "pointer", "&:hover": { backgroundColor: "primary.light", color: "white" } }}
          />
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        <IconButton size="small" onClick={() => onEdit(tag)}>
          <Edit color="info" />
        </IconButton>
        <IconButton size="small" onClick={() => onDelete(tag)}>
          <DeleteOutline color="error" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const Tags = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [deletingAllTags, setDeletingAllTags] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);

  const fetchTags = useCallback(async () => {
    try {
      const { data } = await api.get("/tags/", { params: { searchParam, pageNumber } });
      dispatch({ type: "LOAD_TAGS", payload: data.tags });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err, t);
    }
  }, [searchParam, pageNumber, t]);

  useEffect(() => { dispatch({ type: "RESET" }); setPageNumber(1); }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delay = setTimeout(fetchTags, 500);
    return () => clearTimeout(delay);
  }, [searchParam, pageNumber, fetchTags]);

  useEffect(() => {
    const socket = openSocket();
    socket.on("tags", (data) => {
      if (data.action === "update" || data.action === "create") dispatch({ type: "UPDATE_TAGS", payload: data.tags });
      if (data.action === "delete") dispatch({ type: "DELETE_TAG", payload: +data.tagId });
    });
    return () => socket.disconnect();
  }, []);

  const handleOpenTagModal = () => { setSelectedTag(null); setTagModalOpen(true); };
  const handleCloseTagModal = (tagData) => {
    setSelectedTag(null);
    setTagModalOpen(false);
    if (tagData) dispatch({ type: "UPDATE_TAGS", payload: tagData });
  };
  const handleSearch = (e) => setSearchParam(e.target.value.toLowerCase());
  const handleEditTag = (tag) => { setSelectedTag(tag); setTagModalOpen(true); };
  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(t("tags.toasts.deleted"));
      dispatch({ type: "DELETE_TAG", payload: tagId });
    } catch (err) { toastError(err, t); }
    setDeletingTag(null);
  };
  const handleDeleteAllTags = async () => {
    try {
      await api.delete(`/tags`);
      toast.success(t("tags.toasts.deletedAll"));
      dispatch({ type: "RESET" });
    } catch (err) { toastError(err, t); }
    setDeletingAllTags(null);
  };
  const loadMore = () => setPageNumber((p) => p + 1);
  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };
  const handleViewContacts = (tag) => navigate("/contacts", { state: { tagFilter: tag } });

  // Separate into own vs imported
  const ownTags = tags.filter((t) => !t.isImported);
  const importedTags = tags.filter((t) => t.isImported);

  const tableHeaders = (
    <TableHead>
      <TableRow>
        <TableCell align="center">{t("tags.table.id")}</TableCell>
        <TableCell align="center">{t("tags.table.name")}</TableCell>
        <TableCell align="center">{t("tags.table.color")}</TableCell>
        <TableCell align="center">{t("tags.table.contacts")}</TableCell>
        <TableCell align="center">{t("tags.table.actions")}</TableCell>
      </TableRow>
    </TableHead>
  );

  const rowProps = {
    onEdit: handleEditTag,
    onDelete: (tag) => { setDeletingTag(tag); setConfirmModalOpen(true); },
    onViewContacts: handleViewContacts,
    t,
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingTag ? t("tags.confirmationModal.deleteTitle") : t("tags.confirmationModal.deleteAllTitle")}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => deletingTag ? handleDeleteTag(deletingTag.id) : handleDeleteAllTags(deletingAllTags)}
      >
        {deletingTag ? t("tags.confirmationModal.deleteMessage") : t("tags.confirmationModal.deleteAllMessage")}
      </ConfirmationModal>

      <TagModal
        open={tagModalOpen}
        onClose={handleCloseTagModal}
        reload={fetchTags}
        aria-labelledby="form-dialog-title"
        tagId={selectedTag?.id}
      />

      <MainHeader>
        <Title>{t("tags.title")} {tags.length > 0 ? `(${tags.length})` : ""}</Title>
        <MainHeaderButtonsWrapper>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1, flexWrap: "wrap" }}>
            <TextField
              placeholder={t("contacts.searchPlaceholder")}
              type="search"
              size="small"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="secondary" />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, minWidth: 200, "& .MuiOutlinedInput-root": { height: 40 } }}
            />
            <Tooltip title={t("tags.buttons.add")}>
              <Button variant="contained" color="primary" onClick={handleOpenTagModal} sx={{ height: 40 }}>
                <AddCircleOutline />
              </Button>
            </Tooltip>
            <Tooltip title={t("tags.buttons.deleteAll")}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => { setConfirmModalOpen(true); setDeletingAllTags(tags); }}
                sx={{ height: 40 }}
              >
                <DeleteForever />
              </Button>
            </Tooltip>
          </Box>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <MainPaper variant="outlined" onScroll={handleScroll}>

        {/* ── Suas Etiquetas ── */}
        <SectionHeader>
          <PersonIcon sx={{ fontSize: "1.1rem", color: theme.palette.text.secondary }} />
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.72rem", letterSpacing: 0.5 }}>
            Suas Etiquetas
          </Typography>
          <Chip label={ownTags.length} size="small" sx={{ height: 18, fontSize: "0.65rem" }} />
        </SectionHeader>

        <Table size="small">
          {tableHeaders}
          <TableBody>
            {ownTags.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                    Nenhuma etiqueta criada
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              ownTags.map((tag) => <TagRow key={tag.id} tag={tag} {...rowProps} />)
            )}
            {loading && <TableRowSkeleton columns={5} />}
          </TableBody>
        </Table>

        {/* ── Importadas do Speed Mode ── */}
        {(importedTags.length > 0 || (!loading && importedTags.length === 0 && ownTags.length > 0)) && (
          <>
            <Divider sx={{ mt: 2 }} />
            <SectionHeader imported>
              <BoltIcon sx={{ fontSize: "1.1rem", color: theme.palette.primary.main }} />
              <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ textTransform: "uppercase", fontSize: "0.72rem", letterSpacing: 0.5 }}>
                Importadas via Speed Mode
              </Typography>
              <Chip
                label={importedTags.length}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: 18, fontSize: "0.65rem" }}
              />
            </SectionHeader>

            {importedTags.length === 0 ? (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                  Nenhuma etiqueta importada ainda. Aparecem aqui ao importar um Speed Mode com etapas de etiqueta.
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                {tableHeaders}
                <TableBody>
                  {importedTags.map((tag) => <TagRow key={tag.id} tag={tag} {...rowProps} />)}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </MainPaper>
    </MainContainer>
  );
};

export default Tags;
